import { useToastController } from "@tamagui/toast";
import type { DocumentPickerAsset } from "expo-document-picker";
import type { ImagePickerAsset } from "expo-image-picker";
import { useCallback } from "react";

import { useUploadAttachments } from "@/api/generated";
import type { AttachmentType } from "@/api/mutations/conversation";
import { useCurrentConversationId } from "@/hooks/conversation";
import { getAssetFilename } from "@/utils/attachments";

export default function useHandleAttachmentsPicked({
  setAttachments,
  attachmentCount,
  attachmentSize,
  setAttachmentCount,
  setAttachmentSize,
}: {
  setAttachments: React.Dispatch<React.SetStateAction<AttachmentType[]>>;
  attachmentCount: number;
  attachmentSize: number;
  setAttachmentCount: (count: number) => void;
  setAttachmentSize: (size: number) => void;
}) {
  const toast = useToastController();
  const currentConversationId = useCurrentConversationId();
  const { mutate: uploadAttachments, isPending: isUploading } =
    useUploadAttachments();

  const handleAttachmentsPicked = useCallback(
    async (
      assets: (DocumentPickerAsset | ImagePickerAsset)[],
      files: File[],
    ) => {
      const newFilesSize = files.reduce((sum, file) => sum + file.size, 0);
      const newAttachments = assets.map((a, i) => ({
        localUri: a.uri,
        fileSize: files[i].size,
        name: getAssetFilename(a),
        mimeType: a.mimeType,
        isImage: !!a.mimeType?.startsWith("image/"),
      }));
      setAttachmentCount(attachmentCount + newAttachments.length);
      setAttachmentSize(attachmentSize + newFilesSize);
      setAttachments((prev) => [...prev, ...newAttachments]);

      uploadAttachments(
        {
          data: {
            files,
            // This hook can run before a conversation exists (e.g. uploading
            // from the homepage), so conversation_id may be absent.
            ...(currentConversationId
              ? { conversation_id: currentConversationId }
              : {}),
          },
        },
        {
          onSuccess: (data) => {
            setAttachments((prev) => {
              const newAttachmentIds = [...data.attachment_ids];
              const newPresignedUrls = [...data.presigned_urls];
              const justAddedUris = new Set(
                newAttachments.map((a) => a.localUri),
              );

              return prev.map((attachment) => {
                if (
                  !attachment.attachmentId &&
                  justAddedUris.has(attachment.localUri)
                ) {
                  return {
                    ...attachment,
                    attachmentId: newAttachmentIds.shift(),
                    presignedUrl: newPresignedUrls.shift(),
                  };
                }
                return attachment;
              });
            });
          },
          onError: () => {
            toast.show(
              "There was an error uploading your files. Please try again.",
              { duration: 10000 },
            );
            const failedFilesSize = newAttachments.reduce(
              (sum, attachment) => sum + (attachment.fileSize || 0),
              0,
            );
            setAttachmentCount(attachmentCount - newAttachments.length);
            setAttachmentSize(attachmentSize - failedFilesSize);
            setAttachments((prev) =>
              prev.filter(
                (a) => !newAttachments.some((na) => na.localUri === a.localUri),
              ),
            );
          },
        },
      );
    },
    [
      attachmentCount,
      attachmentSize,
      setAttachmentCount,
      setAttachmentSize,
      setAttachments,
      uploadAttachments,
      toast,
      currentConversationId,
    ],
  );

  return { handleAttachmentsPicked, isUploading };
}
